import { isEmpty } from 'lodash';
import { OptionListOption } from '@gaiads/telia-react-component-library';
import { Address, AdminOrganizationRequest, Person } from '@cdaas/api-clients/dist/asset-admin-openapi';
import { SalesChannel } from '@cdaas/api-clients/dist/product-service-openapi';
import { EMAIL_OR_EMPTY_STRING_REGEX, EMAIL_REGEX } from '../../utils';

export interface CompanyInformationErrors {
  tscId?: string;
  companyName?: string;
  businessId?: string;
  vatId?: string;
  teliaFinanceNumber?: string;
  finnishSorviId?: string;
  technicalContactEmail?: string;
}

export interface BillingAddressErrors {
  street?: string;
  city?: string;
  zip?: string;
  country?: string;
}

export interface BillingContactPersonErrors {
  first_name?: string;
  last_name?: string;
  country_calling_code?: string;
  phone_number?: string;
  email?: string;
}

export interface SalesChannelErrors {
  salesChannel?: string;
}

export interface Errors {
  companyInformation: CompanyInformationErrors;
  billingAddress: BillingAddressErrors;
  billingContact: BillingContactPersonErrors;
  salesChannel: SalesChannelErrors;
  companyDevicePolicyLink?: string;
  parentOrganization?: string;
  customerBusinessIdForNorway?: string;
}

export const httpsUrlRegex = /^$|^https:\/\/.+/;

export const setParentOrganizationError = (t: (T: string) => string): Errors => {
  return { companyInformation: {}, billingAddress: {}, billingContact: {}, salesChannel: {}, parentOrganization: t('parent_organization.api_error') };
};

export const setCustomerBusinessIdNorwayError = (t: (T: string) => string): Errors => {
  return {
    companyInformation: {},
    billingAddress: {},
    billingContact: {},
    salesChannel: {},
    customerBusinessIdForNorway: t('regionSelection.businessIdNotFoundNorwayError'),
  };
};

export const setCustomerBusinessIdAlreadyExists = (t: (T: string) => string): Errors => {
  return {
    companyInformation: {},
    billingAddress: {},
    billingContact: {},
    salesChannel: {},
    customerBusinessIdForNorway: t('regionSelection.businessIdAlreadyExistsError'),
  };
};

export const validateCompanyInformation = (
  organization: AdminOrganizationRequest,
  region: OptionListOption | undefined,
  t: (T: string) => string,
  errors: Errors,
) => {
  if (!/^[A-Za-z0-9-\s]+$/.test(organization.tsc_id || '')) {
    errors.companyInformation.tscId = t('company_information.tscid_input_invalid_error');
  }
  if (!organization.tsc_id) {
    errors.companyInformation.tscId = t('company_information.tscid_input_required_error');
  }
  if (!organization.name) {
    errors.companyInformation.companyName = t('company_information.company_name_input_required_error');
  }
  if (!organization.vat_number) {
    errors.companyInformation.vatId = t('company_information.customer_vat_id_input_required_error');
  }
  if (!organization.organization_number) {
    errors.companyInformation.businessId = t('company_information.customer_business_id_input_required_error');
  }
  if (!organization.call_off_number) {
    errors.companyInformation.teliaFinanceNumber = t('company_information.telia_finance_call_off_number_input_required_error');
  }
  if (region?.value === 'FI' && !organization.epicor_contract_id) {
    errors.companyInformation.finnishSorviId = t('company_information.sorvi_id_required_error');
  }
  if (organization.technical_contact_email && !EMAIL_OR_EMPTY_STRING_REGEX.exec(organization.technical_contact_email)) {
    errors.companyInformation.technicalContactEmail = t('company_information.technical_contact_email_input_invalid_error');
  }
  if (organization.company_device_policy_url && !httpsUrlRegex.exec(organization.company_device_policy_url)) {
    errors.companyDevicePolicyLink = t('company_device_policy.company_device_policy_input_error');
  }
};

const validateBillingAddress = (billingAddress: Address, t: (T: string) => string, errors: Errors) => {
  if (!billingAddress?.street) {
    errors.billingAddress.street = t('billing_information.street_input_required_error');
  }
  if (!billingAddress?.city) {
    errors.billingAddress.city = t('billing_information.city_input_required_error');
  }
  if (!billingAddress?.zip) {
    errors.billingAddress.zip = t('billing_information.zip_input_required_error');
  }
  if (!billingAddress?.country) {
    errors.billingAddress.country = t('billing_information.country_input_required_error');
  }
};

const validateBillingContact = (billingContact: Person, t: (T: string) => string, errors: Errors) => {
  if (!billingContact?.first_name) {
    errors.billingContact.first_name = t('billing_information.first_name_input_required_error');
  }
  if (!billingContact?.last_name) {
    errors.billingContact.last_name = t('billing_information.last_name_input_required_error');
  }
  if (!billingContact?.country_calling_code) {
    errors.billingContact.country_calling_code = t('billing_information.country_calling_code_input_required_error');
  }
  if (!billingContact?.phone_number) {
    errors.billingContact.phone_number = t('billing_information.phone_number_input_required_error');
  }
  if (!billingContact?.email) {
    errors.billingContact.email = t('billing_information.email_input_required_error');
  }
  if (!errors.billingContact.email && !EMAIL_REGEX.exec(billingContact?.email || '')) {
    errors.billingContact.email = t('billing_information.invalid_email_error');
  }
};

const validateSalesChannel = (organization: AdminOrganizationRequest, t: (T: string) => string, errors: Errors) => {
  if (!organization.sales_channel) {
    errors.salesChannel.salesChannel = t('sales_channel.sales_channel_input_required_error');
  }
};

export const validateCustomerOrganization = (
  organization: AdminOrganizationRequest,
  salesChannel: SalesChannel | undefined,
  region: OptionListOption | undefined,
  t: (T: string) => string,
): { valid: boolean; errors: Errors } => {
  const errors: Errors = {
    companyInformation: {},
    billingAddress: {},
    billingContact: {},
    salesChannel: {},
  };

  validateCompanyInformation(organization, region, t, errors);
  validateBillingAddress(organization.default_billing_address, t, errors);
  validateBillingContact(organization.default_billing_contact, t, errors);
  validateSalesChannel(organization, t, errors);

  let valid = true;
  for (const [, value] of Object.entries(errors)) {
    if (!isEmpty(value)) {
      valid = false;
      break;
    }
  }

  return { valid, errors };
};
